<template>
  <nav class="navbar is-fixed-top box" role="navigation" aria-label="main navigation">
    <div class="navbar-brand">
      <RouterLink class="navbar-item" :to="{ name: 'home' }">
        <img src="/images/logoMark.svg">
        <strong class="is-hidden-touch">&nbsp;&nbsp;Dungeon Express</strong>
      </RouterLink>

      <a role="button" :class="{ 'navbar-burger': true, 'is-active': showMobileNav }" @click="showMobileNav = !showMobileNav">
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
        <span aria-hidden="true"></span>
      </a>
    </div>

    <div :class="{ 'navbar-menu': !showMobileNav }">
      <div class="navbar-start">
        <RouterLink v-if="characters.length && hostState.status === OFFLINE && joinState.status === OFFLINE" :to="{ name: 'listCharacters' }" class="navbar-item">Characters</RouterLink>
        <RouterLink v-if="hostState.status === JOINED" :to="{ name: 'room' }" class="navbar-item">Room</RouterLink>
        <RouterLink v-else-if="joinState.status === JOINED" :to="{ name: 'playCharacter', params: { id: joinState.characterId } }" class="navbar-item">Play</RouterLink>
      </div>

      <div class="navbar-end">
        <div v-if="joinState.status === JOINED" class="navbar-item">
          <div :class="{ 'dropdown': true, 'is-active': joinMenuOpen }">
            <div class="dropdown-trigger">
              <button class="button is-text" @click="joinMenuOpen = !joinMenuOpen">
                <span class="statusIndicator" />
                Joined&nbsp;<strong>{{ joinState.code }}</strong>
              </button>
            </div>
            <div class="dropdown-menu">
              <div class="dropdown-content">
                <span v-for="item in combatOrder" :key="item.character.id" class="dropdown-item">
                  <template v-if="joinState.characterId === item.character.id"><strong>{{ item.character.name }} (You)</strong></template>
                  <template v-else-if="item.isNpc"><em>{{ item.character.name }} (NPC)</em></template>
                  <template v-else>{{ item.character.name }}</template>
                </span>
                <hr class="dropdown-divider">
                <a class="dropdown-item" @click="doLeaveRoom">
                  Leave Room
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hostState.status === JOINED" class="navbar-item">
          <div :class="{ 'dropdown': true, 'is-active': hostMenuOpen }">
            <div class="dropdown-trigger">
              <button class="button is-text" @click="hostMenuOpen = !hostMenuOpen">
                <span class="statusIndicator" />
                Hosting&nbsp;<strong>{{ hostState.code }}</strong>
              </button>
            </div>
            <div class="dropdown-menu">
              <div class="dropdown-content">
                <a class="dropdown-item" @click="copyRoomCode">
                  Copy Room Code
                </a>
                <hr class="dropdown-divider">
                <a class="dropdown-item" @click="doCloseRoom">
                  Close Room
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="navbar-item" id="navbarEnd" />
      </div>
    </div>
  </nav>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import useCharacters from '@/hooks/useCharacters'
import { useHost, useJoin, JOINED, OFFLINE } from '@/hooks/room'
import useToast from '@/hooks/useToast'
import copyToClipboard from '@/helpers/copyToClipboard'

export default defineComponent({
  setup () {
    const { characters } = useCharacters()
    const { state: hostState, closeRoom } = useHost()
    const route = useRoute()
    const router = useRouter()
    const toast = useToast()

    const hostMenuOpen = ref(false)
    const doCloseRoom = () => {
      hostMenuOpen.value = false
      if (window.confirm('Are you sure you want to close the room?')) {
        router.push({ name: 'home' })
        closeRoom()
      }
    }

    const { state: joinState, leaveRoom } = useJoin()
    const joinMenuOpen = ref(false)

    const copyRoomCode = () => {
      toast('Room code copied.')
      copyToClipboard(hostState.code)
      hostMenuOpen.value = false
    }

    const doLeaveRoom = () => {
      leaveRoom()
      hostMenuOpen.value = false
    }

    const combatOrder = computed(() => joinState.combatOrder.map((id) => {
      const character = joinState.characters.find((c) => c.id === id) || joinState.npcs.find((n) => n.id === id)
      const isNpc = !!joinState.npcs.find((n) => n.id === id)
      return {
        character,
        isNpc 
      }
    }))

    const showMobileNav = ref(false)

    watch(route, () => {
      showMobileNav.value = false
    })

    return {
      characters,
      hostState,
      hostMenuOpen,
      joinState,
      joinMenuOpen,
      JOINED,
      OFFLINE,
      doCloseRoom,
      copyRoomCode,
      doLeaveRoom,
      combatOrder,
      showMobileNav
    }
  }
})
</script>

<style lang="sass" scoped>
.box
  border-radius: 0
  padding: 0

@keyframes statusIndicator
  0%
    box-shadow: 0 0 0 0em rgba(#e64a00, 0.65)
  65%
    box-shadow: 0 0 0 0.65em rgba(#e64a00, 0)

.statusIndicator
  display: inline-flex
  width: 0.5em
  height: 0.5em
  border-radius: 100px
  background-color: var(--primary)
  margin-right: 0.75em
  animation-name: statusIndicator
  animation-duration: 2.5s
  animation-iteration-count: infinite
  // border: solid 2px #fff
  box-sizing: content-box

.dropdown-item
  white-space: nowrap

@media screen and (max-width: 1023px)
  .navbar-end
    .navbar-item
      display: flex
      align-items: center
      justify-content: center
      &:empty
        padding: 0
</style>
